// packages/web-ui/src/AppShell/useTabBarKeyboardNav.ts
//
// W2-S4 AC #6 — WAI-ARIA Authoring Practices keyboard map for the
// `<TabBar>` `role="tablist"`:
//   ←/→             — focus prev / next (with wrap-around)
//   Home / End      — focus first / last
//   Enter / Space   — activate the focused tab
//   Delete          — close focused tab (skipped on pinned)
//
// `Ctrl+W`, `Ctrl+Tab`, `Ctrl+Shift+Tab` are GLOBAL (see useTabKeybindings.ts);
// this hook only handles within-tablist navigation.

import { useCallback, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { TabState } from '../state/navigation.js';

export interface TabBarKeyboardHandlers {
  onActivate: (tabId: string) => void;
  onClose: (tabId: string) => void;
}

export type TabBarKeyDownHandler = (event: ReactKeyboardEvent<HTMLElement>) => void;

/**
 * Builds the `keydown` handler attached to the `<TabBar role="tablist">` div.
 * The caller manages a `focusedIndex` state and the actual `tab.focus()`
 * imperative call (the handler returns the next focused index via
 * `setFocusedIndex` and lets the caller's effect imperatively focus the
 * matching DOM element).
 */
export function useTabBarKeyboardNav(
  tabs: TabState[],
  focusedIndex: number,
  setFocusedIndex: (i: number) => void,
  handlers: TabBarKeyboardHandlers,
): TabBarKeyDownHandler {
  return useCallback(
    (event) => {
      if (tabs.length === 0) return;
      const { key } = event;

      const move = (next: number): void => {
        event.preventDefault();
        event.stopPropagation();
        setFocusedIndex(next);
      };

      if (key === 'ArrowRight') {
        const next = focusedIndex < 0 ? 0 : (focusedIndex + 1) % tabs.length;
        move(next);
        return;
      }
      if (key === 'ArrowLeft') {
        const next = focusedIndex < 0 ? tabs.length - 1 : (focusedIndex - 1 + tabs.length) % tabs.length;
        move(next);
        return;
      }
      if (key === 'Home') {
        move(0);
        return;
      }
      if (key === 'End') {
        move(tabs.length - 1);
        return;
      }
      if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
        const target = focusedIndex < 0 ? 0 : focusedIndex;
        const tab = tabs[target];
        if (tab !== undefined) {
          event.preventDefault();
          event.stopPropagation();
          handlers.onActivate(tab.id);
        }
        return;
      }
      if (key === 'Delete') {
        const tab = focusedIndex < 0 ? undefined : tabs[focusedIndex];
        if (tab !== undefined && !tab.pinned) {
          event.preventDefault();
          event.stopPropagation();
          handlers.onClose(tab.id);
        }
      }
    },
    [tabs, focusedIndex, setFocusedIndex, handlers],
  );
}
